import React, { useReducer, useEffect, useCallback, memo, useState } from 'react';
import { Tag } from 'antd';
import { Modal } from 'antd';
import { Tabs } from 'antd';
import { Typography } from 'antd';
import produce from 'immer';
import _capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import { useCampaignList, useCampaignsAutoFillStampsOnIds } from 'src/api/campaigns';
import { TableMainText, TableSecondaryText } from 'styles';

import { SMALL_PAGE_SIZE } from 'appConstants';
import { TableTimestamp, SearchBar, StatusTag } from 'components';
import { TableWithNoWrapHeader } from 'components/StyledTable';
import { IAnyCampaign } from 'containers/Campaign/types';
import { TCustomerDetails } from 'containers/Customer/i18n/TModules';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import i18n from 'utils/i18n';

interface CampaignSelectModalState {
  state: any;
  page: any;
  order: any;
  size: any;
  sort_by: any;
  search_string: any;
  campaign_type: any;
  eligible_user_account_id: any;
  filter_not_completed: boolean;
}
const paramsReducer = (state: CampaignSelectModalState, action): CampaignSelectModalState =>
  produce(state, draft => {
    switch (action.type) {
      case 'CAMPAIGN_TYPE':
        if (action.value === 'quest') {
          draft['campaign_type'] = 'quest';
          draft['state'] = action.includeScheduled ? ['active', 'scheduled'] : 'active';
        } else if (action.value === 'progress') {
          draft['campaign_type'] = 'progress';
          draft['state'] = action.includeScheduled ? ['active', 'scheduled'] : 'active';
        } else if (action.value === 'quiz' || action.value === 'survey') {
          draft['campaign_type'] = 'game';
          draft['game_type'] = action.value;
          draft['state'] = null;
        } else if (action.value === 'stamp') {
          draft['campaign_type'] = 'stamp';
          // Filter selectable states server-side so pagination reflects only eligible
          // campaigns; a page of ended/inactive ones otherwise renders empty. The BE
          // expects `state` as a comma-separated string (param[:state].split(',')) — an
          // array is dropped by strong params and the filter is silently ignored (PLA-8931).
          draft['state'] = 'active,draft,approved';
          delete draft['game_type'];
        } else {
          draft['campaign_type'] = action.value;
          draft['state'] = null;
          delete draft['game_type'];
        }
        break;
      case 'STATE':
        draft.state = action.value === 'all' ? null : action.value;
        break;
      case 'TABLE':
        draft.page = action.pagination;
        if (Object.keys(action.sorter).length) {
          draft['sort_by'] = action.sorter.columnKey;
          draft.order = action.sorter.order === 'ascend' ? 'asc' : 'desc';
        }
        break;
      case 'PAGINATION':
        draft.page = action.current;
        if (action.size) {
          draft.size = action.size;
        }

        break;
      case 'SEARCH':
        draft['search_string'] = action.value;
        break;
      case 'FILTER_NOT_COMPLETED':
        draft.filter_not_completed = action.value;
        if (action.value) {
          draft.state = ['active', 'draft'];
        } else {
          draft.state = null;
        }
        break;
      default:
        break;
    }
  });

const CampaignSelectModal = ({
  isOpen = false,
  closeModal,
  onChooseCampaign,
  campaignType = null,
  hasMultiRowSelect = false,
  tabs,
  eligableUserAccountId = null,
  alreadyChosenCampaignsKeys = [],
  otherParams = {},
  includeScheduledStates = false,
  excludeAutoFillStamps = false,
  helperText = null
}) => {
  const [selectedCampaignKeys, setSelectedCampaignKeys] = useState(alreadyChosenCampaignsKeys);
  const [params, paramsDispatch] = useReducer(paramsReducer, {
    state: !tabs ? null : tabs[0] === 'all' ? null : tabs[0],
    page: 1,
    order: 'asc',
    sort_by: null,
    search_string: '',
    size: SMALL_PAGE_SIZE,
    campaign_type: campaignType,
    eligible_user_account_id: eligableUserAccountId,
    filter_not_completed: false,
    ...otherParams
  });
  const { data, meta, isLoading, isValidating } = useCampaignList(params);

  const stateFilteredCampaigns = (data ?? []).filter(campaign =>
    ['active', 'draft', 'approved'].includes(campaign.state)
  );

  // PLA-8931: when picking a campaign for the "Stamps Issued" trigger, auto-fill stamp
  // campaigns never emit that event, so exclude them from the selectable list.
  const autoFillCheckIds =
    isOpen && excludeAutoFillStamps ? stateFilteredCampaigns.map(campaign => campaign.id) : [];
  const { autoFillOnIds, isLoading: autoFillLoading } =
    useCampaignsAutoFillStampsOnIds(autoFillCheckIds);

  const dataSource = excludeAutoFillStamps
    ? stateFilteredCampaigns.filter(campaign => !autoFillOnIds.has(campaign.id))
    : stateFilteredCampaigns;

  const loading = isLoading || isValidating || (excludeAutoFillStamps && autoFillLoading);

  const onChange = pagination => {
    paramsDispatch({ type: 'PAGINATION', current: pagination.current });
  };

  const onTabChange = tab => paramsDispatch({ type: 'STATE', value: tab });

  const onSelectCampaignChange = useCallback(selectedCampaignKeys => {
    setSelectedCampaignKeys(selectedCampaignKeys);
  }, []);

  const otherParamsState = otherParams['state'];

  useEffect(() => {
    paramsDispatch({
      type: 'CAMPAIGN_TYPE',
      value: campaignType,
      includeScheduled: includeScheduledStates
    });
  }, [campaignType, includeScheduledStates]);

  useEffect(() => {
    if (otherParamsState) {
      paramsDispatch({ type: 'STATE', value: otherParamsState });
    }
  }, [otherParamsState]);

  useEffect(() => {
    if (isOpen && tabs && !otherParamsState) {
      // Reset to the first tab when the modal opens (unless overridden by otherParams)
      const initialState = tabs[0] === 'all' ? null : tabs[0];
      paramsDispatch({ type: 'STATE', value: initialState });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { t } = useMultiLanguageTranslation();
  useEffect(() => {
    if (otherParams['state']) {
      paramsDispatch({ type: 'STATE', value: otherParams['state'] });
    }
  }, [otherParams]);

  return (
    <Modal
      maskClosable={false}
      width={1200}
      title={t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.Title`)}
      open={isOpen}
      onOk={
        hasMultiRowSelect
          ? () => {
              onChooseCampaign(selectedCampaignKeys);
            }
          : closeModal
      }
      onCancel={closeModal}
      okText={t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.OkText`)}
      cancelText={t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.CancelText`)}
    >
      <div data-testid="CampaignSelectModal">
        <SearchBar
          //@ts-ignore
          onSearch={value => paramsDispatch({ type: 'SEARCH', value })}
          placeholder={t(
            `${TCustomerDetails}.CustomerStamps.CampaignSelectModal.SearchPlaceholder`
          )}
          style={{ marginBottom: '0.5rem' }}
        />
        {helperText && (
          <Typography.Text
            type="secondary"
            data-testid="campaign-select-helper"
            style={{ display: 'block', marginBottom: '0.5rem' }}
          >
            {helperText}
          </Typography.Text>
        )}
        {tabs && (
          <Tabs
            activeKey={!params.state ? 'all' : params.state}
            onChange={onTabChange}
            tabBarStyle={{ marginBottom: 0 }}
            items={tabs.map(tab => {
              const itemTab = tab === 'approved' ? 'scheduled' : tab;
              return {
                key: tab,
                label: i18n.exists(
                  `${TCustomerDetails}.CustomerStamps.CampaignSelectModal.${_capitalize(itemTab)}`
                )
                  ? t(
                      `${TCustomerDetails}.CustomerStamps.CampaignSelectModal.${_capitalize(
                        itemTab
                      )}`
                    )
                  : _capitalize(itemTab)
              };
            })}
          />
        )}
        <TableWithNoWrapHeader
          scroll={{ y: 430 }}
          rowKey={'id'}
          loading={loading}
          columns={[
            {
              title: t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.Name`),
              key: 'name',
              render: (_, record: IAnyCampaign) => (
                <>
                  <TableMainText>{record.name}</TableMainText>
                  <TableSecondaryText>
                    {t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.ID`)}: {record.id}
                  </TableSecondaryText>
                </>
              )
            },
            {
              title: t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.Status`),
              key: 'state',
              dataIndex: 'state',
              render: state => <StatusTag state={state} />
            },
            {
              title: t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.Tags`),
              key: 'tags',
              render: (_, record: IAnyCampaign) => (
                <>
                  {record.tags.map(tag => (
                    <Tag key={tag.id}>{tag.name}</Tag>
                  ))}
                </>
              )
            },
            {
              title: t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.StartDate`),
              key: 'start_date',
              render: (_, record) => <TableTimestamp timestamp={record['start_date']} />
            },
            {
              title: t(`${TCustomerDetails}.CustomerStamps.CampaignSelectModal.EndDate`),
              key: 'end_date',
              render: (_, record) => <TableTimestamp timestamp={record['end_date']} />
            }
          ]}
          dataSource={dataSource}
          onChange={onChange}
          onRow={
            !hasMultiRowSelect
              ? (record, _) => ({
                  onClick: () => {
                    onChooseCampaign(record);
                    closeModal();
                  }
                })
              : null
          }
          rowSelection={
            hasMultiRowSelect
              ? {
                  selectedRowKeys: selectedCampaignKeys,
                  onChange: onSelectCampaignChange
                }
              : null
          }
          showHeader
          pagination={{
            showSizeChanger: true,
            onShowSizeChange: (current, size) =>
              paramsDispatch({ type: 'PAGINATION', current, size }),
            showQuickJumper: true,
            current: params.page,
            position: ['bottomRight'],
            defaultPageSize: SMALL_PAGE_SIZE,
            total: meta?.count ?? meta?.total_count ?? 0
          }}
        />
      </div>
    </Modal>
  );
};

CampaignSelectModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  hasMultiRowSelect: PropTypes.bool,
  onChooseCampaign: PropTypes.func,
  campaignType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOf([
        'stamp',
        'game',
        'invite',
        'give_reward',
        'quiz',
        'survey',
        'referral',
        'progress'
      ])
    )
  ]),
  tabs: PropTypes.array,
  eligableUserAccountId: PropTypes.number,
  eventName: PropTypes.string,
  includeScheduledStates: PropTypes.bool,
  excludeAutoFillStamps: PropTypes.bool,
  helperText: PropTypes.node
};

export default memo(CampaignSelectModal);
